<template>
  <v-row class="fill-height white ma-0 flex-nowrap custom-max-height">
    <v-col cols="auto" class="pa-0 z-2">
      <UserSessionsDrawer
        @selected-session="loadChatHistory"
        @create-session="clearChatHistory"
        @rename-session="renameSession"
        @remove-session="removeSession"
        @load-more-sessions="loadUserSessions"
        :sessions="userSessions"
        :loading="loadingSessions"
        ref="userSessionsDrawer"
      />
    </v-col>

    <v-col
      :class="[
        { 'align-self-center': messages.length === 0 },
        { 'mobile-left-padding' : this.$vuetify.breakpoint.smAndDown },
        { 'flex-grow-0 responsive-width' : this.rateCardProject}
      ]"
      class="white"
    >
      <v-card
        class="fill-height chat-card"
        flat
        dense
      >
        <v-card-text
          :class="{
            'active-chat-messages flex-grow-1': messages.length > 0
          }"
          class="chat-messages px-2 px-sm-4"
          ref="chatMessages"
        >
          <div v-if="! loading && messages.length >= messagesLimit" class="text-center py-5">
            <v-btn @click="loadChatHistory(selectedSession)" block small text>
              <v-icon color="primary">mdi-refresh</v-icon>{{$t('load_more') }}
            </v-btn>
          </div>

          <div v-if="loading && messages.length === 0" class="text-center py-5">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2">{{ $t('loading') }}</div>
          </div>

          <div v-else-if="messages.length === 0" class="text-center py-5">
            <v-icon
              :color="this.agentConfig.icon_color ? this.agentConfig.icon_color : 'primary'"
              class="mb-1"
              size="74"
            >{{ this.agentConfig.hero ? this.agentConfig.hero : 'mdi-robot-happy' }}</v-icon>
            <div
              class="text-h6 text-sm-h5 text-lg-h4 font-weight-bold text--primary"
            >{{ this.agentConfig.title ? $t(this.agentConfig.title) : $t('rate_card_title') }}</div>
            <div class="text-caption text-sm-body-2 text-lg-h6 font-weight-regular">{{ this.agentConfig.subtitle ? $t(this.agentConfig.subtitle) : $t('rate_card_subtitle') }}</div>
          </div>

          <template v-else>
            <component
              v-for="(message, index) in [...messages].reverse()"
              @view-details="showRateCardProjectDetails"
              :is="isProjectJson(message) ? 'ChatMessageBubble' : 'MessageBubble'"
              :key="index"
              :message="message"
              :is-user="message.isUser"
            />
          </template>
        </v-card-text>

        <v-card-actions class="pa-0">
          <ChatInput
            @send-message="sendChatMessage"
            :sending-message="sendingMessage"
            ref="chatInput"
          />
        </v-card-actions>
      </v-card>
    </v-col>

    <v-col v-if="rateCardProject">
      <ChatProjectDetailView
        :project="rateCardProject"
        @close="rateCardProject = null"
        @create-job="createJob"
      />
    </v-col>
  </v-row>
</template>

<style scoped>
  .chat-container {
    display: flex;
    flex-direction: row;
    padding: 16px;
  }

  .chat-card {
    display: flex;
    flex-direction: column;
    max-width: 790px;
    margin: 0 auto;
    width: 100%;
  }

  .chat-messages {
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
  }

  .active-chat-messages {
    height: 73vh;
    padding-top: 46px !important;
    padding-bottom: 12px !important;
  }

  .z-2 {
    z-index: 2;
  }

  .mobile-left-padding {
    padding-left: calc(56px + 12px);
  }

  .custom-max-height {
    max-height: calc(100vh - 64px);
  }
  
  .responsive-width {
    display: none;
  }

  @media (min-width: 960px) {
    .responsive-width {
      display: block;
      width: 38%;
    }
  }

  @media (min-width: 1300px) {
    .responsive-width {
      display: block;
      width: 33%;
    }
  }

  @media (min-width: 1700px) {
    .responsive-width {
      display: block;
      width: 28%;
    }
  }
</style>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import RateCardActions from '@/store/ai_workforce/chat/actions-types'

import MessageBubble from '@/components/ai_workforce/chat/MessageBubble.vue'
import ChatMessageBubble from '@/components/ai_workforce/chat/ChatMessageBubble.vue'
import ChatProjectDetailView from '@/components/ai_workforce/chat/ChatProjectDetailView.vue'

import ChatInput from '@/components/ai_workforce/chat/ChatInput.vue'
import UserSessionsDrawer from '@/components/ai_workforce/chat/UserSessionsDrawer.vue'

export default {
  name: 'ChatView',

  components: {
    MessageBubble,
    ChatMessageBubble,
    ChatProjectDetailView,
    ChatInput,
    UserSessionsDrawer,
  },

  props: {
    initialPrompt: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      sendingMessage: false,
      selectedSession: null,
      loadingSessions: false,
      userSessionOffset: 0,
      userSessionLimit: 100,
      messagesOffset: 0,
      messagesLimit: 200,
      messages: [],
      rateCardProject: null,
      initialPromptSent: false,
    }
  },

  created() {
    this.loadUserSessions()

    // Check for initial prompt from props or route query
    const initialPrompt = this.initialPrompt || this.$route.query.initialPrompt
    if (initialPrompt && !this.initialPromptSent) {
      this.$nextTick(() => {
        this.sendInitialPrompt(initialPrompt)
      })
    }
  },

  updated() {
    this.scrollToBottom()
  },

  computed: {
    ...mapGetters("app", {
      getUserApplicationById: "getUserApplicationById"
    }),

    ...mapState('ai_workforce/chat', {
      userSessions: state => state.userSessions,
    }),

    ...mapState('app', {
      selectedAiWorkforceApp: state => state.selectedAiWorkforceApp,
    }),

    agentConfig() {
      const agent = this.getUserApplicationById(this.selectedAiWorkforceApp)

      return this.parseJson(agent.config)
    },
  },

  methods: {
    ...mapActions('ai_workforce/chat', [
      RateCardActions.GET_CHAT_HISTORY,
      RateCardActions.SEND_MESSAGE,
      RateCardActions.GET_USER_SESSIONS,
      RateCardActions.REMOVE_USER_SESSION,
      RateCardActions.RENAME_USER_SESSION,
    ]),

    loadUserSessions() {
      this.loadingSessions = true

      const params = {
        offset: this.userSessionOffset,
        limit: this.userSessionLimit,
      }

      this[RateCardActions.GET_USER_SESSIONS](params)
        .then((response) => {
          const count = response.length || 0

          this.userSessionOffset += count
        })
        .finally(() => {
          this.loadingSessions = false
        })
    },

    renameSession(session) {
      this[RateCardActions.RENAME_USER_SESSION](session)
    },

    removeSession(session) {
      this[RateCardActions.REMOVE_USER_SESSION](session)
    },

    loadChatHistory(sessionId) {
      if (this.selectedSession != sessionId) {
        this.clearChatHistory()
      }

      this.selectedSession = sessionId
      this.loading = true

      const params = {
        sessionId,
        offset: this.messagesOffset,
        limit: this.messagesLimit,
      }

      this[RateCardActions.GET_CHAT_HISTORY](params)
        .then((response) => {
          const messages = Object.keys(response[0]).length > 0 ? response : []

          const messagesCount = messages.length || 0
          this.messagesOffset += messagesCount

          if (messagesCount > 0) {
            const newMessages = messages.map(msg => ({
              text: msg.message.content,
              isUser: msg.message.type === "human",
              timestamp: new Date(msg.created_at)
            }))

            this.messages = [...this.messages, ...newMessages]
          }
        })
        .finally(() => {
          this.loading = false

          this.$nextTick(() => {
            this.scrollToBottom()
          })
        })
    },

    sendChatMessage(message) {
      const messageText = message.trim()
      if (!messageText || this.sendingMessage) {
        return
      }

      const attachmentFile = this.$refs.chatInput.getFile() || null

      const userMessage = {
        text: (attachmentFile ? `${messageText} Seguem os dados do Anexo:` : messageText),
        isUser: true,
        timestamp: new Date()
      }

      this.messages.unshift(userMessage)
      this.sendingMessage = true

      let sendMessageData = {
        chatInput: messageText,
        sessionId: this.selectedSession,
        rootNodeId: this.agentConfig.root_node_id || null,
      }

      sendMessageData = {
        ...sendMessageData,
        ...(attachmentFile && { file: attachmentFile }),
      }

      this[RateCardActions.SEND_MESSAGE](sendMessageData)
        .then((response) => {
          const responseMessage = {
            text: response.output,
            isUser: false,
            timestamp: userMessage.timestamp
          }

          this.messages.unshift(responseMessage)

          this.selectedSession = response.sessionId
          this.$refs.userSessionsDrawer.setSelectedSession(response.sessionId)
        })
        .finally(() => {
          this.sendingMessage = false

          this.$nextTick(() => {
            this.scrollToBottom()
          })
        })
    },

    createJob() {
      this.$refs.chatInput.clearFile()

      this.sendChatMessage("Criar Job")
    },

    clearChatHistory() {
      this.messages = []
      this.selectedSession = null
      this.rateCardProject = null
      this.messagesOffset = 0
      this.initialPromptSent = false
    },

    scrollToBottom() {
      if (this.$refs.chatMessages) {
        const element = this.$refs.chatMessages
        element.scrollTop = element.scrollHeight
      }
    },

    isProjectJson(message) {
      if (message.isUser) {
        return false
      }

      let str = message.text

      try {
        str = str.replace(/[^]*```json\n/g, "").replace(/\n```([\s\S]*)/g, "")

        const json = JSON.parse(str)

        return json.tipo_objeto === 'project'
      } catch (e) {
        return false
      }
    },

    parseJson(str)
    {
      try {
        return JSON.parse(str)
      } catch (e) {
        return {}
      }
    },

    showRateCardProjectDetails(json) {
      this.rateCardProject = json
      this.$refs.userSessionsDrawer.collapseDrawer()
    },

    sendInitialPrompt(promptText) {
      if (this.initialPromptSent || !promptText.trim()) {
        return
      }

      this.initialPromptSent = true
      this.sendingMessage = true

      const sendMessageData = {
        chatInput: promptText.trim(),
        sessionId: this.selectedSession,
      }

      this[RateCardActions.SEND_MESSAGE](sendMessageData)
        .then((response) => {
          // Only add the AI response to the chat, not the initial prompt
          const responseMessage = {
            text: response.output,
            isUser: false,
            timestamp: new Date()
          }

          this.messages.unshift(responseMessage)

          this.selectedSession = response.sessionId
          this.$refs.userSessionsDrawer.setSelectedSession(response.sessionId)
        })
        .finally(() => {
          this.sendingMessage = false

          this.$nextTick(() => {
            this.scrollToBottom()
          })
        })
    },
  },
}
</script>
